/**
 * Memoization Utilities
 *
 * Optimized memoization utilities for GeoJSON conversion and
 * expensive map data transformations.
 *
 * Phase 8.1.3: Optimize GeoJSON conversion with memoization
 */

import { useMemo, useRef, useCallback } from 'react';
import type { SensorDataPoint, SensorGeoJSON, SensorFeature } from '../MapComponent.types';

// ============================================================================
// Types
// ============================================================================

export interface MemoizedGeoJSONResult {
  geoJson: SensorGeoJSON;
  /** Time taken for conversion in ms */
  conversionTime: number;
  /** Whether result was from cache */
  fromCache: boolean;
  /** Number of features in result */
  featureCount: number;
}

export interface GeoJSONCacheEntry {
  geoJson: SensorGeoJSON;
  sensorIds: string[];
  timestamp: number;
}

// ============================================================================
// Constants
// ============================================================================

/** Maximum cache entries for GeoJSON results */
const MAX_CACHE_ENTRIES = 10;

/** Cache entry TTL in ms (5 minutes) */
const CACHE_TTL = 5 * 60 * 1000;

// ============================================================================
// Core Functions
// ============================================================================

/**
 * Convert a single sensor to a GeoJSON feature
 *
 * @param sensor - Sensor data point
 * @returns GeoJSON point feature
 */
export function sensorToFeature(sensor: SensorDataPoint): SensorFeature {
  return {
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: [sensor.longitude, sensor.latitude],
    },
    properties: {
      id: sensor.id,
      deviceId: sensor.deviceId,
      moisture: sensor.moistureLevel,
      temperature: sensor.temperature,
      zone: sensor.zone,
      timestamp: typeof sensor.timestamp === 'string'
        ? sensor.timestamp
        : sensor.timestamp?.toISOString?.() || new Date().toISOString(),
    },
  };
}

/**
 * Convert sensor array to GeoJSON FeatureCollection
 *
 * Optimized conversion with early returns and minimal object creation.
 *
 * @param sensors - Array of sensor data points
 * @returns GeoJSON FeatureCollection
 */
export function sensorsToGeoJSON(sensors: SensorDataPoint[]): SensorGeoJSON {
  const startTime = performance.now();

  // Pre-allocate array for better performance
  const features = new Array<SensorFeature>(sensors.length);

  for (let i = 0; i < sensors.length; i++) {
    const sensor = sensors[i];
    features[i] = {
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [sensor.longitude, sensor.latitude],
      },
      properties: {
        id: sensor.id,
        deviceId: sensor.deviceId,
        moisture: sensor.moistureLevel,
        temperature: sensor.temperature,
        zone: sensor.zone,
        timestamp: typeof sensor.timestamp === 'string'
          ? sensor.timestamp
          : sensor.timestamp?.toISOString?.() || new Date().toISOString(),
      },
    };
  }

  return {
    type: 'FeatureCollection',
    features,
  };
}

/**
 * Generate a cache key from sensor data
 *
 * Uses a fast hash of sensor IDs to detect data changes.
 *
 * @param sensors - Sensor array
 * @returns Cache key string
 */
export function generateCacheKey(sensors: SensorDataPoint[]): string {
  if (sensors.length === 0) return 'empty';

  // Use sensor count + first/last IDs + sum of moisture for quick hash
  const first = sensors[0];
  const last = sensors[sensors.length - 1];
  let moistureSum = 0;

  for (let i = 0; i < sensors.length; i += Math.max(1, Math.floor(sensors.length / 10))) {
    moistureSum += sensors[i].moistureLevel;
  }

  return `${sensors.length}-${first.id}-${last.id}-${Math.round(moistureSum)}`;
}

/**
 * Check if sensor arrays have same data (for memoization)
 *
 * @param prev - Previous sensor array
 * @param next - Next sensor array
 * @returns True if arrays contain same data
 */
export function areSensorsEqual(
  prev: SensorDataPoint[] | null | undefined,
  next: SensorDataPoint[]
): boolean {
  if (!prev) return false;
  if (prev.length !== next.length) return false;
  if (prev === next) return true;

  // Check first and last elements
  if (prev[0]?.id !== next[0]?.id) return false;
  if (prev[prev.length - 1]?.id !== next[next.length - 1]?.id) return false;

  // Spot check a few random elements
  const checkCount = Math.min(5, prev.length);
  for (let i = 0; i < checkCount; i++) {
    const idx = Math.floor(i * prev.length / checkCount);
    if (
      prev[idx]?.id !== next[idx]?.id ||
      prev[idx]?.moistureLevel !== next[idx]?.moistureLevel
    ) {
      return false;
    }
  }

  return true;
}

// ============================================================================
// LRU Cache Implementation
// ============================================================================

/**
 * Simple LRU cache for GeoJSON results
 */
export class GeoJSONCache {
  private cache: Map<string, GeoJSONCacheEntry>;
  private maxSize: number;
  private ttl: number;

  constructor(maxSize: number = MAX_CACHE_ENTRIES, ttl: number = CACHE_TTL) {
    this.cache = new Map();
    this.maxSize = maxSize;
    this.ttl = ttl;
  }

  get(key: string): SensorGeoJSON | null {
    const entry = this.cache.get(key);

    if (!entry) {
      return null;
    }

    // Check TTL
    if (Date.now() - entry.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    // Move to end (most recently used)
    this.cache.delete(key);
    this.cache.set(key, entry);

    return entry.geoJson;
  }

  set(key: string, geoJson: SensorGeoJSON, sensorIds: string[]): void {
    // Remove oldest entry if at capacity
    if (this.cache.size >= this.maxSize) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, {
      geoJson,
      sensorIds,
      timestamp: Date.now(),
    });
  }

  clear(): void {
    this.cache.clear();
  }

  size(): number {
    return this.cache.size;
  }

  getStats(): { size: number; maxSize: number; ttl: number } {
    return {
      size: this.cache.size,
      maxSize: this.maxSize,
      ttl: this.ttl,
    };
  }
}

// Global cache instance
const globalGeoJSONCache = new GeoJSONCache();

// ============================================================================
// Hooks
// ============================================================================

/**
 * Hook for memoized GeoJSON conversion
 *
 * Uses a multi-level caching strategy:
 * 1. React useMemo for same reference
 * 2. Deep equality check for same data
 * 3. LRU cache for recent conversions
 *
 * @param sensors - Sensor data array
 * @returns Memoized GeoJSON result with stats
 *
 * @example
 * ```typescript
 * const { geoJson, conversionTime, fromCache } = useMemoizedGeoJSON(sensorData);
 *
 * // geoJson is only recalculated when sensor data actually changes
 * ```
 */
export function useMemoizedGeoJSON(
  sensors: SensorDataPoint[]
): MemoizedGeoJSONResult {
  const prevSensorsRef = useRef<SensorDataPoint[] | null>(null);
  const prevResultRef = useRef<MemoizedGeoJSONResult | null>(null);

  return useMemo(() => {
    const startTime = performance.now();

    // Check if sensors are the same as previous
    if (prevSensorsRef.current && areSensorsEqual(prevSensorsRef.current, sensors)) {
      if (prevResultRef.current) {
        return {
          ...prevResultRef.current,
          fromCache: true,
          conversionTime: 0,
        };
      }
    }

    // Check LRU cache
    const cacheKey = generateCacheKey(sensors);
    const cached = globalGeoJSONCache.get(cacheKey);

    if (cached) {
      const result: MemoizedGeoJSONResult = {
        geoJson: cached,
        conversionTime: performance.now() - startTime,
        fromCache: true,
        featureCount: cached.features.length,
      };
      prevSensorsRef.current = sensors;
      prevResultRef.current = result;
      return result;
    }

    // Perform conversion
    const geoJson = sensorsToGeoJSON(sensors);
    const conversionTime = performance.now() - startTime;

    // Cache result
    globalGeoJSONCache.set(
      cacheKey,
      geoJson,
      sensors.map((s) => s.id)
    );

    const result: MemoizedGeoJSONResult = {
      geoJson,
      conversionTime,
      fromCache: false,
      featureCount: geoJson.features.length,
    };

    prevSensorsRef.current = sensors;
    prevResultRef.current = result;

    return result;
  }, [sensors]);
}

/**
 * Hook for incremental GeoJSON updates
 *
 * Only updates features that have changed, rather than
 * rebuilding the entire FeatureCollection.
 *
 * @param sensors - Sensor data array
 * @param recentlyUpdated - Set of recently updated sensor IDs
 * @returns Optimized GeoJSON with incremental updates
 */
export function useIncrementalGeoJSON(
  sensors: SensorDataPoint[],
  recentlyUpdated: Set<string>
): SensorGeoJSON {
  const geoJsonRef = useRef<SensorGeoJSON | null>(null);
  const sensorMapRef = useRef<Map<string, number>>(new Map());

  return useMemo(() => {
    const startTime = performance.now();

    // First time: build full GeoJSON and index
    if (!geoJsonRef.current || sensorMapRef.current.size !== sensors.length) {
      const features: SensorFeature[] = [];
      const sensorMap = new Map<string, number>();

      for (let i = 0; i < sensors.length; i++) {
        features.push(sensorToFeature(sensors[i]));
        sensorMap.set(sensors[i].id, i);
      }

      geoJsonRef.current = { type: 'FeatureCollection', features };
      sensorMapRef.current = sensorMap;

      return geoJsonRef.current;
    }

    // Incremental update: only update changed sensors
    if (recentlyUpdated.size > 0) {
      const features = [...geoJsonRef.current.features];
      let updatedCount = 0;

      for (const sensorId of recentlyUpdated) {
        const featureIndex = sensorMapRef.current.get(sensorId);
        if (featureIndex !== undefined) {
          const sensor = sensors.find((s) => s.id === sensorId);
          if (sensor) {
            features[featureIndex] = sensorToFeature(sensor);
            updatedCount++;
          }
        }
      }

      if (updatedCount > 0) {
        geoJsonRef.current = { type: 'FeatureCollection', features };
      }
    }

    return geoJsonRef.current;
  }, [sensors, recentlyUpdated]);
}

/**
 * Create a stable callback for GeoJSON conversion
 *
 * Useful for passing to child components without causing re-renders.
 *
 * @returns Stable conversion function
 */
export function useGeoJSONConverter() {
  return useCallback((sensors: SensorDataPoint[]): SensorGeoJSON => {
    return sensorsToGeoJSON(sensors);
  }, []);
}

/**
 * Clear the global GeoJSON cache
 *
 * Call this when doing a full data refresh or on component unmount.
 */
export function clearGeoJSONCache(): void {
  globalGeoJSONCache.clear();
}

/**
 * Get GeoJSON cache statistics
 */
export function getGeoJSONCacheStats(): { size: number; maxSize: number; ttl: number } {
  return globalGeoJSONCache.getStats();
}

export default {
  sensorToFeature,
  sensorsToGeoJSON,
  generateCacheKey,
  areSensorsEqual,
  GeoJSONCache,
  useMemoizedGeoJSON,
  useIncrementalGeoJSON,
  useGeoJSONConverter,
  clearGeoJSONCache,
  getGeoJSONCacheStats,
};
